import { useState } from "react";
import Layout from "../components/Layout";
import {
  Globe, Server, Shield, Mail, CheckCircle2,
  ChevronDown, ChevronRight, Copy, Terminal,
  Rocket, Settings, Database
} from "lucide-react";
import { toast } from "sonner";

const steps = [
  {
    id: 1,
    title: "Configurer le DNS chez Squarespace",
    icon: Globe,
    color: "bg-sky-600",
    content: [
      {
        type: "text",
        value: "Votre domaine cmes-pharma.org est enregistré chez Squarespace. Vous devez le faire pointer vers votre serveur Contabo (IP : 94.72.108.94)."
      },
      {
        type: "steps",
        items: [
          "Connectez-vous à votre compte Squarespace (squarespace.com)",
          "Allez dans Settings > Domains > cmes-pharma.org",
          "Cliquez sur DNS Settings",
          "Supprimez les enregistrements A existants (ex: 198.185.159.xxx)",
          "Ajoutez un nouvel enregistrement A :"
        ]
      },
      {
        type: "dns",
        records: [
          { type: "A", name: "@", value: "94.72.108.94", ttl: "3600" },
          { type: "A", name: "www", value: "94.72.108.94", ttl: "3600" },
        ]
      },
      {
        type: "info",
        value: "La propagation DNS prend entre 1 et 24 heures. Vous pouvez vérifier avec la commande : ping cmes-pharma.org"
      }
    ]
  },
  {
    id: 2,
    title: "Se connecter au serveur Contabo",
    icon: Server,
    color: "bg-emerald-600",
    content: [
      {
        type: "text",
        value: "Connectez-vous à votre serveur Contabo en SSH depuis votre ordinateur."
      },
      {
        type: "command",
        title: "Connexion SSH (depuis votre terminal)",
        code: "ssh root@94.72.108.94"
      },
      {
        type: "info",
        value: "Utilisez le mot de passe fourni par Contabo lors de la création du serveur. Si vous êtes sous Windows, utilisez PowerShell ou installez PuTTY."
      },
      {
        type: "command",
        title: "Mettre à jour le système",
        code: "apt update && apt upgrade -y"
      }
    ]
  },
  {
    id: 3,
    title: "Installer Docker sur le serveur",
    icon: Database,
    color: "bg-violet-600",
    content: [
      {
        type: "text",
        value: "Docker permet de lancer l'application facilement sans configuration complexe. Exécutez ces commandes sur le serveur :"
      },
      {
        type: "command",
        title: "1. Installer Docker",
        code: "curl -fsSL https://get.docker.com | sh\nsystemctl enable docker\nsystemctl start docker"
      },
      {
        type: "command",
        title: "2. Installer Docker Compose",
        code: "apt install -y docker-compose-plugin git"
      },
      {
        type: "command",
        title: "3. Vérifier l'installation",
        code: "docker --version\ndocker compose version"
      },
      {
        type: "info",
        value: "Si les deux commandes affichent un numéro de version, l'installation est réussie."
      }
    ]
  },
  {
    id: 4,
    title: "Télécharger et configurer le projet",
    icon: Settings,
    color: "bg-amber-600",
    content: [
      {
        type: "text",
        value: "Téléchargez le code source sur le serveur et configurez les variables d'environnement de production."
      },
      {
        type: "command",
        title: "1. Télécharger le code (via GitHub)",
        code: "mkdir -p /var/www\ncd /var/www\ngit clone [URL_DE_VOTRE_REPO_GITHUB] cmes-pharma\ncd cmes-pharma"
      },
      {
        type: "info",
        value: "Si vous n'utilisez pas GitHub, copiez les fichiers depuis votre ordinateur avec : scp -r ./projet root@94.72.108.94:/var/www/cmes-pharma"
      },
      {
        type: "command",
        title: "2. Créer le fichier de configuration production",
        code: "cp backend/.env.production.example backend/.env.production\nnano backend/.env.production"
      },
      {
        type: "text",
        value: "Modifiez le fichier .env.production avec vos vraies clés :"
      },
      {
        type: "command",
        title: "Contenu du fichier .env.production",
        code: "MONGO_URL=mongodb://mongodb:27017\nDB_NAME=cmes_pharma_prod\nJWT_SECRET=GENEREZ_UNE_CLE_LONGUE_ET_ALEATOIRE\nCORS_ORIGINS=https://cmes-pharma.org,https://www.cmes-pharma.org\nINFOBIP_API_KEY=VOTRE_CLE_INFOBIP\nINFOBIP_BASE_URL=VOTRE_BASE_URL_INFOBIP\nSTRIPE_API_KEY=votre_cle_stripe_live\nFRONTEND_URL=https://cmes-pharma.org"
      },
      {
        type: "info",
        value: "IMPORTANT : Remplacez JWT_SECRET par une longue chaîne aléatoire (32+ caractères). Pour Stripe, utilisez votre clé LIVE (sk_live_...) au lieu de la clé de test."
      }
    ]
  },
  {
    id: 5,
    title: "Lancer l'application",
    icon: Rocket,
    color: "bg-red-600",
    content: [
      {
        type: "text",
        value: "Lancez toute l'application avec une seule commande Docker Compose."
      },
      {
        type: "command",
        title: "Démarrer tous les services",
        code: "cd /var/www/cmes-pharma\ndocker compose up -d --build"
      },
      {
        type: "text",
        value: "Cette commande va automatiquement :"
      },
      {
        type: "steps",
        items: [
          "Démarrer la base de données MongoDB",
          "Construire et démarrer le backend (API Python/FastAPI)",
          "Construire et démarrer le frontend (React)",
          "Configurer le serveur web Nginx (proxy inverse)",
          "Démarrer le renouvellement automatique du certificat SSL"
        ]
      },
      {
        type: "command",
        title: "Vérifier que tout fonctionne",
        code: "docker compose ps\ndocker compose logs --tail=50"
      },
      {
        type: "info",
        value: "Tous les services doivent afficher 'running'. Le premier lancement prend 3-5 minutes pour construire les images."
      }
    ]
  },
  {
    id: 6,
    title: "Obtenir le certificat SSL (HTTPS)",
    icon: Shield,
    color: "bg-teal-600",
    content: [
      {
        type: "text",
        value: "Le certificat SSL est obligatoire pour les paiements Stripe et la sécurité des données. Let's Encrypt fournit des certificats gratuits."
      },
      {
        type: "command",
        title: "Obtenir le certificat SSL",
        code: "docker compose run --rm certbot certonly \\\n  --webroot --webroot-path=/var/www/certbot \\\n  -d cmes-pharma.org -d www.cmes-pharma.org \\\n  --email contact@cmes-pharma.org \\\n  --agree-tos --no-eff-email"
      },
      {
        type: "command",
        title: "Redémarrer Nginx avec le SSL",
        code: "docker compose restart nginx"
      },
      {
        type: "steps",
        items: [
          "Le certificat est installé automatiquement",
          "Il se renouvelle automatiquement tous les 90 jours",
          "Votre site est maintenant accessible en HTTPS"
        ]
      },
      {
        type: "info",
        value: "Après cette étape, votre site est accessible sur https://cmes-pharma.org"
      }
    ]
  },
  {
    id: 7,
    title: "Configurer les emails (Infobip)",
    icon: Mail,
    color: "bg-pink-600",
    content: [
      {
        type: "text",
        value: "Les notifications email sont envoyées via Infobip. Pour une meilleure délivrabilité, vérifiez votre domaine dans Infobip."
      },
      {
        type: "steps",
        items: [
          "Connectez-vous à votre compte Infobip (portal.infobip.com)",
          "Allez dans Channels > Email > Domain Vérification",
          "Ajoutez le domaine cmes-pharma.org",
          "Copiez les enregistrements DNS fournis par Infobip",
          "Ajoutez ces enregistrements (SPF, DKIM) dans Squarespace DNS Settings",
          "Attendez la vérification (quelques heures)",
          "Les emails seront envoyés depuis noreply@cmes-pharma.org"
        ]
      },
      {
        type: "dns",
        records: [
          { type: "TXT", name: "@", value: "v=spf1 include:spf.infobip.com ~all", ttl: "3600" },
        ]
      },
      {
        type: "info",
        value: "L'enregistrement SPF ci-dessus est un exemple. Utilisez les valeurs exactes fournies par Infobip dans leur interface."
      }
    ]
  },
];

const costSummary = [
  { item: "Domaine cmes-pharma.org (Squarespace)", cost: "~12 000 FCFA/an" },
  { item: "Serveur Contabo VPS", cost: "~96 000 FCFA/an" },
  { item: "Certificat SSL (Let's Encrypt)", cost: "Gratuit" },
  { item: "Email transactionnel (Infobip)", cost: "Inclus" },
];

const commandsRef = [
  { label: "Démarrer l'application", cmd: "docker compose up -d" },
  { label: "Arrêter l'application", cmd: "docker compose down" },
  { label: "Voir les logs", cmd: "docker compose logs --tail=100 -f" },
  { label: "Redémarrer un service", cmd: "docker compose restart backend" },
  { label: "Mettre à jour le code", cmd: "git pull && docker compose up -d --build" },
  { label: "Sauvegarder la base de données", cmd: "docker exec cmes-mongodb mongodump --out /data/backup" },
];

export default function DeploymentGuidePage() {
  const [openStep, setOpenStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  const toggleComplete = (stepId) => {
    setCompletedSteps(prev =>
      prev.includes(stepId) ? prev.filter(id => id !== stepId) : [...prev, stepId]
    );
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papiers");
  };

  const progress = Math.round((completedSteps.length / steps.length) * 100);

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="deployment-guide-page">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-100" data-testid="deployment-title">
            Guide de Déploiement
          </h1>
          <p className="text-slate-400 mt-1">
            Déployer cmes-pharma.org sur votre serveur Contabo (94.72.108.94)
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8 bg-slate-800/50 rounded-xl border border-slate-700 p-5" data-testid="deployment-progress">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-slate-300">Progression</span>
            <span className="text-sm font-bold text-sky-400">{completedSteps.length}/{steps.length} étapes</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-3">
            <div
              className="bg-sky-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="progress-bar"
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3 mb-10" data-testid="deployment-steps">
          {steps.map((step) => {
            const Icon = step.icon;
            const isOpen = openStep === step.id;
            const isComplete = completedSteps.includes(step.id);

            return (
              <div
                key={step.id}
                className={`bg-slate-800/50 rounded-xl border transition-all ${
                  isComplete
                    ? "border-emerald-500/40"
                    : isOpen
                    ? "border-sky-500/40 shadow-lg shadow-sky-500/5"
                    : "border-slate-700"
                }`}
                data-testid={`step-${step.id}`}
              >
                {/* Step Header */}
                <button
                  onClick={() => setOpenStep(isOpen ? null : step.id)}
                  className="w-full p-4 flex items-center gap-4 text-left"
                  data-testid={`step-${step.id}-header`}
                >
                  <div className={`w-10 h-10 rounded-lg ${isComplete ? "bg-emerald-500" : step.color} flex items-center justify-center flex-shrink-0`}>
                    {isComplete ? <CheckCircle2 className="w-5 h-5 text-white" /> : <Icon className="w-5 h-5 text-white" />}
                  </div>
                  <div className="flex-1">
                    <span className="text-xs text-slate-500 font-medium">Étape {step.id}</span>
                    <h3 className={`font-semibold ${isComplete ? "text-emerald-400" : "text-slate-200"}`}>{step.title}</h3>
                  </div>
                  {isOpen ? <ChevronDown className="w-5 h-5 text-slate-500" /> : <ChevronRight className="w-5 h-5 text-slate-500" />}
                </button>

                {/* Step Content */}
                {isOpen && (
                  <div className="px-4 pb-5 space-y-4 border-t border-slate-700 pt-4">
                    {step.content.map((block, i) => {
                      if (block.type === "text") return (
                        <p key={i} className="text-sm text-slate-400 leading-relaxed">{block.value}</p>
                      );

                      if (block.type === "steps") return (
                        <ol key={i} className="space-y-2">
                          {block.items.map((item, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                              <span className="w-5 h-5 rounded-full bg-sky-500/20 text-sky-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{j + 1}</span>
                              {item}
                            </li>
                          ))}
                        </ol>
                      );

                      if (block.type === "info") return (
                        <div key={i} className="p-3 rounded-lg bg-amber-500/100/10 border border-amber-500/30 text-sm text-amber-300">{block.value}</div>
                      );

                      if (block.type === "command") return (
                        <div key={i} className="rounded-lg overflow-hidden border border-slate-600" data-testid={`command-block-${i}`}>
                          <div className="px-3 py-2 bg-slate-700/50 flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-400">{block.title}</span>
                            <button
                              onClick={() => copyToClipboard(block.code)}
                              className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1"
                              data-testid={`copy-btn-${i}`}
                            >
                              <Copy className="w-3 h-3" /> Copier
                            </button>
                          </div>
                          <pre className="p-3 bg-slate-900 text-emerald-400 text-xs overflow-x-auto whitespace-pre-wrap font-mono">{block.code}</pre>
                        </div>
                      );

                      if (block.type === "dns") return (
                        <div key={i} className="rounded-lg border border-slate-600 overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-700/50">
                              <tr>
                                {["Type", "Nom", "Valeur", "TTL"].map(h => (
                                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-slate-400">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {block.records.map((r, j) => (
                                <tr key={j} className="border-t border-slate-700">
                                  <td className="px-3 py-2 font-mono text-sky-400 text-xs">{r.type}</td>
                                  <td className="px-3 py-2 font-mono text-xs text-slate-300">{r.name}</td>
                                  <td className="px-3 py-2 font-mono text-xs text-slate-300">{r.value}</td>
                                  <td className="px-3 py-2 text-xs text-slate-500">{r.ttl}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );

                      return null;
                    })}

                    <button
                      onClick={() => toggleComplete(step.id)}
                      className={`mt-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        isComplete
                          ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30"
                          : "bg-sky-600 text-white hover:bg-sky-500"
                      }`}
                      data-testid={`step-${step.id}-complete`}
                    >
                      {isComplete ? "Marquer comme non terminé" : "Marquer comme terminé"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick Commands Référence */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 mb-6" data-testid="commands-référence">
          <h3 className="font-bold text-slate-200 mb-4 flex items-center gap-2">
            <Terminal className="w-4 h-4 text-sky-400" />
            Commandes utiles
          </h3>
          <div className="grid gap-2">
            {commandsRef.map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-900/50 border border-slate-700/50">
                <span className="text-sm text-slate-400">{item.label}</span>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-emerald-400 font-mono">{item.cmd}</code>
                  <button
                    onClick={() => copyToClipboard(item.cmd)}
                    className="text-slate-500 hover:text-sky-400 transition-colors"
                    data-testid={`copy-cmd-${i}`}
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cost Summary */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 mb-6" data-testid="cost-summary">
          <h3 className="font-bold text-slate-200 mb-4">Coûts annuels estimés</h3>
          <div className="space-y-2">
            {costSummary.map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
                <span className="text-sm text-slate-400">{item.item}</span>
                <span className="text-sm font-semibold text-slate-200">{item.cost}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-3 border-t-2 border-slate-600">
              <span className="font-bold text-slate-200">Total estimé</span>
              <span className="font-bold text-sky-400">~108 000 FCFA/an</span>
            </div>
          </div>
        </div>

        {/* Support */}
        <div className="bg-sky-500/10 rounded-xl border border-sky-500/30 p-5 text-center" data-testid="support-section">
          <p className="text-sm text-sky-300 font-medium">Besoin d'aide pour le déploiement ?</p>
          <p className="text-sm text-sky-400/70 mt-1">
            Contactez-nous au <strong className="text-sky-300">+225 07-08-30-23-83</strong> pour un accompagnement personnalisé
          </p>
        </div>
      </div>
    </Layout>
  );
}
